import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import BtNodeBlock from "./BtNodeBlock";
import InsertPopover from "./InsertPopover";
import NodeInspectorCard from "./NodeInspectorCard";
import { getIncompleteBehaviorNodeIds } from "../../behavior/editorIncompleteNodes";
import { getPaletteGroupsForBehaviorEditor } from "../../behavior/context/rulesRegistry";
import { useSceneBehaviorContextStore } from "../../stores/sceneBehaviorContextStore";
import { useSceneStore } from "../../stores/sceneStore";
import {
  insertChild,
  insertIfThenElseTemplate,
  deleteNodeFromBehavior,
  moveChild,
  reparentNode,
} from "../../behavior/tree";
import { useShallow } from "zustand/react/shallow";

const DEFAULT_ZOOM = 1;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;

/** Scene graph uses SVG viewBox (vector); HTML needs zoom, not transform:scale(), for sharp text. */
const CAN_USE_CSS_ZOOM =
  typeof globalThis.CSS !== "undefined" &&
  typeof globalThis.CSS.supports === "function" &&
  globalThis.CSS.supports("zoom", "1");

function buildBodiesByUuid(bodies) {
  const out = {};
  Object.values(bodies || {}).forEach((b) => {
    if (b && typeof b === "object" && typeof b.uuid === "string") {
      out[b.uuid] = b;
    }
  });
  return out;
}

function shouldIgnorePanStart(target) {
  if (!(target instanceof globalThis.Element)) return true;
  if (target.closest("[data-behavior-tree-node]")) return true;
  if (target.closest("[data-behavior-inspector]")) return true;
  if (target.closest("button")) return true;
  if (target.closest('input, textarea, select, [contenteditable="true"]')) return true;
  return false;
}

function isKeyboardEditingTarget(target) {
  return (
    target instanceof globalThis.Element &&
    Boolean(target.closest('input, textarea, select, [contenteditable="true"]'))
  );
}

function arraysEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function setsEqual(a, b) {
  if (a === b) return true;
  if (!(a instanceof Set) || !(b instanceof Set)) return false;
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function didNodeStructureChange(prevNode, nextNode) {
  if (!prevNode || !nextNode) return true;
  if (prevNode.kind !== nextNode.kind) return true;
  switch (nextNode.kind) {
    case "sequence":
    case "selector":
      return !arraysEqual(prevNode.children || [], nextNode.children || []);
    case "retry":
    case "repeat":
      return prevNode.child !== nextNode.child;
    case "ifThenElse":
      return (
        prevNode.conditionChild !== nextNode.conditionChild ||
        prevNode.thenChild !== nextNode.thenChild ||
        prevNode.elseChild !== nextNode.elseChild
      );
    default:
      return false;
  }
}

function collectChangedNodeIds(prevNodes, nextNodes, maxCount = 3) {
  const keys = new Set([...Object.keys(prevNodes || {}), ...Object.keys(nextNodes || {})]);
  const changed = [];
  for (const id of keys) {
    if (prevNodes?.[id] !== nextNodes?.[id]) {
      changed.push(id);
      if (changed.length > maxCount) return null;
    }
  }
  return changed;
}

const BehaviorTreeContent = React.memo(
  function BehaviorTreeContent({
    docNodes,
    docRootId,
    nodeStatuses,
    activeNodeId,
    bodiesByUuid,
    incompleteIds,
    setupMessages,
    onSelect,
    onDelete,
    onSlotClick,
    onReorderSequenceChildren,
    activeInsertSlot,
    onDragEnd,
  }) {
    const rootNode = docNodes?.[docRootId] ?? null;
    const dndSensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
      useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    if (!rootNode) {
      return (
        <div className="rounded-md border border-dashed border-gray-700 bg-gray-900/40 p-6 text-center text-xs panel-text-helper">
          Behavior has no root node.
        </div>
      );
    }
    return (
      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <BtNodeBlock
          node={rootNode}
          allNodes={docNodes}
          rootId={docRootId}
          nodeStatuses={nodeStatuses}
          activeNodeId={activeNodeId}
          bodiesByUuid={bodiesByUuid}
          incompleteIds={incompleteIds}
          setupMessages={setupMessages}
          onSelect={onSelect}
          onDelete={onDelete}
          onSlotClick={onSlotClick}
          onReorderSequenceChildren={onReorderSequenceChildren}
          activeInsertSlot={activeInsertSlot}
        />
      </DndContext>
    );
  },
  (prev, next) => {
    if (prev.docRootId !== next.docRootId) return false;
    if (prev.nodeStatuses !== next.nodeStatuses) return false;
    if (prev.activeNodeId !== next.activeNodeId) return false;
    if (prev.bodiesByUuid !== next.bodiesByUuid) return false;
    if (!setsEqual(prev.incompleteIds, next.incompleteIds)) return false;
    if (prev.setupMessages !== next.setupMessages) return false;
    if (prev.activeInsertSlot !== next.activeInsertSlot) return false;

    if (prev.docNodes === next.docNodes) return true;

    // Allow inspector-side numeric scrubbing to avoid invalidating the full tree.
    const changed = collectChangedNodeIds(prev.docNodes, next.docNodes, 32);
    if (!changed) return false;
    if (changed.length === 0) return true;
    for (const nodeId of changed) {
      const prevNode = prev.docNodes?.[nodeId];
      const nextNode = next.docNodes?.[nodeId];
      if (!prevNode || !nextNode) return false;
      if ((prevNode?.label || "") !== (nextNode?.label || "")) return false;
      if (didNodeStructureChange(prevNode, nextNode)) return false;
      if (prevNode.kind !== nextNode.kind) return false;
      // If the actively inspected node changes kind, refresh tree immediately.
      if (nodeId === next.inspectorNodeId && prevNode.kind !== nextNode.kind) return false;
    }
    if (next.inspectorNodeId && prev.inspectorNodeId !== next.inspectorNodeId) {
      return false;
    }
    if (
      changed.includes(next.docRootId) &&
      didNodeStructureChange(prev.docNodes?.[next.docRootId], next.docNodes?.[next.docRootId])
    ) {
      return false;
    }
    if (next.inspectorNodeId && prev.inspectorNodeId === next.inspectorNodeId) {
      const inspectedChanged = changed.includes(next.inspectorNodeId);
      if (inspectedChanged) {
        const prevNode = prev.docNodes[next.inspectorNodeId];
        const nextNode = next.docNodes[next.inspectorNodeId];
        if (
          !didNodeStructureChange(prevNode, nextNode) &&
          (prevNode?.label || "") === (nextNode?.label || "")
        ) {
          return true;
        }
      }
    }
    return true;
  }
);

export default function BehaviorTreeCanvas({
  doc,
  onDocChange,
  bodies,
  activeNodeId,
  nodeStatuses,
}) {
  const [inspectorNodeId, setInspectorNodeId] = useState(null);
  const [popover, setPopover] = useState(null);

  const viewportRef = useRef(null);
  const panLayerRef = useRef(null);
  const zoomLayerRef = useRef(null);
  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(DEFAULT_ZOOM);
  const isPanningRef = useRef(false);
  const wheelStateRef = useRef({ zoom: DEFAULT_ZOOM, pan: { x: 0, y: 0 } });
  const lastPanClientRef = useRef({ x: 0, y: 0 });
  const suppressClearSelectionRef = useRef(false);
  const selectedNodeIdRef = useRef(null);
  const selectedNodeElementRef = useRef(null);
  const docRef = useRef(doc);
  const onDocChangeRef = useRef(onDocChange);
  useEffect(() => {
    docRef.current = doc;
    onDocChangeRef.current = onDocChange;
  }, [doc, onDocChange]);
  const applySelectedNodeVisual = useCallback((nodeId) => {
    if (selectedNodeElementRef.current) {
      selectedNodeElementRef.current.classList.remove("ring-2", "ring-sky-400/80");
    }
    selectedNodeIdRef.current = nodeId || null;
    if (!nodeId || !viewportRef.current) {
      selectedNodeElementRef.current = null;
      return;
    }
    const el = viewportRef.current.querySelector(`[data-behavior-node-id="${nodeId}"]`);
    if (!el) {
      selectedNodeElementRef.current = null;
      return;
    }
    el.classList.add("ring-2", "ring-sky-400/80");
    selectedNodeElementRef.current = el;
  }, []);
  const applyViewportTransform = useCallback(() => {
    const pan = panRef.current;
    const zoom = zoomRef.current;
    if (panLayerRef.current) {
      panLayerRef.current.style.transform = `translate(${pan.x}px, ${pan.y}px)`;
    }
    if (zoomLayerRef.current) {
      if (CAN_USE_CSS_ZOOM) {
        zoomLayerRef.current.style.zoom = String(zoom);
        zoomLayerRef.current.style.transform = "";
      } else {
        zoomLayerRef.current.style.zoom = "";
        zoomLayerRef.current.style.transform = `scale(${zoom})`;
        zoomLayerRef.current.style.transformOrigin = "0 0";
      }
    }
    wheelStateRef.current = { zoom, pan };
  }, []);

  const bodiesByUuid = useMemo(() => buildBodiesByUuid(bodies), [bodies]);
  const sceneForIncomplete = useSceneStore(
    useShallow((s) => ({
      bodies: s.bodies,
      sites: s.sites,
    }))
  );
  const incompleteIds = useMemo(
    () => (doc ? getIncompleteBehaviorNodeIds(doc, sceneForIncomplete) : new Set()),
    [doc, sceneForIncomplete]
  );
  const setupMessages = useMemo(() => {
    const messages = {};
    if (!doc || doc.baselineSnapshot != null) return messages;
    Object.values(doc.nodes || {}).forEach((node) => {
      if (node?.kind === "resetToBaseline") {
        messages[node.id] =
          "No baseline captured - add a Capture Baseline node earlier in the tree";
      }
    });
    return messages;
  }, [doc]);

  const recipeId = useSceneBehaviorContextStore((s) => s.recipeId);
  const scenarioId = useSceneBehaviorContextStore((s) => s.scenarioId);
  const recipeRobotId = useSceneBehaviorContextStore((s) => s.recipeRobotId);
  const worldBodiesLength = useSceneStore((s) => s.worldBodies?.length ?? 0);
  const bodyCount = useSceneStore((s) => Object.keys(s.bodies || {}).length);
  const paletteGroups = useMemo(() => {
    void recipeId;
    void scenarioId;
    void recipeRobotId;
    void worldBodiesLength;
    void bodyCount;
    return getPaletteGroupsForBehaviorEditor();
  }, [recipeId, scenarioId, recipeRobotId, worldBodiesLength, bodyCount]);

  useEffect(() => {
    panRef.current = { x: 0, y: 0 };
    zoomRef.current = DEFAULT_ZOOM;
    applyViewportTransform();
  }, [doc?.id, applyViewportTransform]);

  useEffect(() => {
    if (selectedNodeIdRef.current) {
      applySelectedNodeVisual(selectedNodeIdRef.current);
    }
  }, [doc, applySelectedNodeVisual]);

  useEffect(() => {
    applyViewportTransform();
  }, [applyViewportTransform]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const { zoom: z, pan: p } = wheelStateRef.current;
      if (e.shiftKey || e.altKey) {
        const deltaX = e.shiftKey ? (Math.abs(e.deltaX) > 0 ? e.deltaX : e.deltaY) : 0;
        const deltaY = e.altKey ? (Math.abs(e.deltaY) > 0 ? e.deltaY : e.deltaX) : 0;
        panRef.current = {
          x: p.x - deltaX,
          y: p.y - deltaY,
        };
        applyViewportTransform();
        return;
      }
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z * factor));
      if (newZoom === z) return;

      const rect = el.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;
      const wx = (cursorX - p.x) / z;
      const wy = (cursorY - p.y) / z;
      panRef.current = {
        x: cursorX - wx * newZoom,
        y: cursorY - wy * newZoom,
      };
      zoomRef.current = newZoom;
      applyViewportTransform();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [applyViewportTransform]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isPanningRef.current) return;
      const dx = e.clientX - lastPanClientRef.current.x;
      const dy = e.clientY - lastPanClientRef.current.y;
      lastPanClientRef.current = { x: e.clientX, y: e.clientY };
      if (Math.abs(dx) + Math.abs(dy) > 2) suppressClearSelectionRef.current = true;
      panRef.current = { x: panRef.current.x + dx, y: panRef.current.y + dy };
      applyViewportTransform();
    };
    const onUp = () => {
      if (!isPanningRef.current) return;
      isPanningRef.current = false;
      if (viewportRef.current) {
        viewportRef.current.classList.remove("cursor-grabbing");
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [applyViewportTransform]);

  const handlePanPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (shouldIgnorePanStart(e.target)) return;
    e.preventDefault();
    lastPanClientRef.current = { x: e.clientX, y: e.clientY };
    isPanningRef.current = true;
    if (viewportRef.current) {
      viewportRef.current.classList.add("cursor-grabbing");
    }
  }, []);

  const clearSelection = useCallback(() => {
    applySelectedNodeVisual(null);
    setInspectorNodeId(null);
  }, [applySelectedNodeVisual]);

  const handleBackgroundClick = useCallback(() => {
    if (suppressClearSelectionRef.current) {
      suppressClearSelectionRef.current = false;
      return;
    }
    clearSelection();
  }, [clearSelection]);

  const handleCanvasKeyDown = useCallback(
    (event) => {
      if (event.key !== "Escape" || isKeyboardEditingTarget(event.target)) return;
      if (!selectedNodeIdRef.current && !inspectorNodeId) return;
      event.preventDefault();
      clearSelection();
    },
    [clearSelection, inspectorNodeId]
  );

  const handleSelectNode = useCallback(
    (nodeId) => {
      applySelectedNodeVisual(nodeId);
      setInspectorNodeId(nodeId);
    },
    [applySelectedNodeVisual]
  );

  const handleSlotClick = useCallback((parentId, index, anchorEl) => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    setPopover({ parentId, index, anchorRect: rect });
  }, []);

  const handlePick = useCallback(
    (kind, insertOverrides) => {
      const currentDoc = docRef.current;
      if (!currentDoc || !popover) return;
      const next =
        kind === "templateIfThenElse"
          ? insertIfThenElseTemplate(currentDoc, popover.parentId, popover.index)
          : insertChild(currentDoc, popover.parentId, popover.index, kind, insertOverrides);
      if (next !== currentDoc) onDocChangeRef.current(next);
    },
    [popover]
  );

  const handleDelete = useCallback(
    (nodeId) => {
      const currentDoc = docRef.current;
      if (!currentDoc) return;
      const next = deleteNodeFromBehavior(currentDoc, nodeId);
      if (next !== currentDoc) {
        onDocChangeRef.current(next);
        if (selectedNodeIdRef.current === nodeId) {
          applySelectedNodeVisual(null);
        }
        setInspectorNodeId((prev) => (prev === nodeId ? null : prev));
      }
    },
    [applySelectedNodeVisual]
  );

  const handleReorderSequenceChildren = useCallback((parentId, fromIndex, toIndex) => {
    const currentDoc = docRef.current;
    if (!currentDoc) return;
    const next = moveChild(currentDoc, parentId, fromIndex, toIndex);
    if (next !== currentDoc) onDocChangeRef.current(next);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const currentDoc = docRef.current;
    if (!currentDoc) return;
    const { active, over } = event;
    if (!active || !over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    const activeParentId = active.data.current?.parentId;
    const activeIndex = active.data.current?.index;

    if (overId.startsWith("drop-")) {
      const targetCompositeId = overId.slice("drop-".length);
      if (targetCompositeId === activeParentId) return;
      const next = reparentNode(currentDoc, activeId, targetCompositeId);
      if (next !== currentDoc) onDocChangeRef.current(next);
      return;
    }

    const overParentId = over.data.current?.parentId;
    const overIndex = over.data.current?.index;
    if (!overParentId) return;

    if (overParentId === activeParentId) {
      if (typeof activeIndex !== "number" || typeof overIndex !== "number") return;
      const next = moveChild(currentDoc, activeParentId, activeIndex, overIndex);
      if (next !== currentDoc) onDocChangeRef.current(next);
      return;
    }

    const next = reparentNode(
      currentDoc,
      activeId,
      overParentId,
      typeof overIndex === "number" ? overIndex : undefined
    );
    if (next !== currentDoc) onDocChangeRef.current(next);
  }, []);

  if (!doc) return null;
  const activeInsertSlot = popover ? { parentId: popover.parentId, index: popover.index } : null;

  return (
    <div
      className="relative h-full w-full overflow-hidden focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sky-400/70"
      onClick={handleBackgroundClick}
      onKeyDown={handleCanvasKeyDown}
      role="region"
      aria-label="Behavior tree canvas"
      tabIndex={0}
    >
      <div
        ref={viewportRef}
        className="relative h-full w-full"
        onPointerDown={handlePanPointerDown}
      >
        <div
          ref={panLayerRef}
          className="mx-auto min-h-full w-max max-w-[calc(100%+4rem)] cursor-auto px-5 py-4"
        >
          <div
            ref={zoomLayerRef}
            className="origin-top-left"
            style={
              CAN_USE_CSS_ZOOM
                ? { zoom: DEFAULT_ZOOM }
                : {
                    transform: `scale(${DEFAULT_ZOOM})`,
                    transformOrigin: "0 0",
                  }
            }
          >
            <BehaviorTreeContent
              docNodes={doc.nodes}
              docRootId={doc.rootId}
              nodeStatuses={nodeStatuses}
              activeNodeId={activeNodeId}
              bodiesByUuid={bodiesByUuid}
              incompleteIds={incompleteIds}
              setupMessages={setupMessages}
              onSelect={handleSelectNode}
              onDelete={handleDelete}
              onSlotClick={handleSlotClick}
              onReorderSequenceChildren={handleReorderSequenceChildren}
              activeInsertSlot={activeInsertSlot}
              inspectorNodeId={inspectorNodeId}
              onDragEnd={handleDragEnd}
            />
          </div>
        </div>
      </div>

      <div
        className="pointer-events-none absolute bottom-2 left-3 z-10 max-w-[min(100%-1.5rem,280px)] text-[10px] leading-snug text-gray-400 select-none"
        aria-hidden="true"
      >
        Scroll: zoom · Drag empty space: pan · Shift/Alt+scroll: pan
      </div>

      {popover ? (
        <InsertPopover
          anchorRect={popover.anchorRect}
          groups={paletteGroups}
          onPick={handlePick}
          onClose={() => setPopover(null)}
        />
      ) : null}

      {inspectorNodeId && doc.nodes[inspectorNodeId] ? (
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <NodeInspectorCard
            node={doc.nodes[inspectorNodeId]}
            bodies={bodies}
            behaviorDoc={doc}
            onApplyDocument={onDocChange}
            onClose={(event) => {
              event?.stopPropagation?.();
              setInspectorNodeId(null);
            }}
            onDelete={() => handleDelete(inspectorNodeId)}
            canDelete={inspectorNodeId !== doc.rootId}
            onChange={(nextNode) => {
              onDocChange({
                ...doc,
                nodes: { ...doc.nodes, [nextNode.id]: nextNode },
              });
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
